/**
 * Comment threads in Monaco: a highlight over each thread's range, a gutter
 * marker that opens the thread, a "Comment"
 * action on the selection, and the open thread's card in a content widget so
 * it scrolls with the code. Clicking into commented code only moves the
 * caret: editing stays undisturbed.
 */
import type { Monaco } from "@monaco-editor/react";
import type { editor } from "monaco-editor";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

// Decorations take class names, not elements, and Monaco turns `:`, `/`,
// `.` and `[` in them into spaces: only plain Tailwind classes survive.
const HIGHLIGHT = "rounded-sm bg-amber-100";
const HIGHLIGHT_ACTIVE = "rounded-sm bg-amber-200";
const GLYPH = "cursor-pointer scale-50 rounded-full bg-amber-500";

export interface MonacoComments {
  threads: {
    id: string;
    /** Offsets into the model's text. */
    range: { start: number; end: number };
  }[];
  activeId: string | null;
  onActivate(id: string): void;
  /** Start a thread on the selection. */
  onComment(selection: { start: number; end: number; quote: string }): void;
  /** The open thread's card. */
  card: ReactNode;
}

export function useMonacoComments(
  ed: editor.IStandaloneCodeEditor | null,
  monaco: Monaco | null,
  comments: MonacoComments | undefined,
  ready: boolean,
): ReactNode {
  const ref = useRef(comments);
  ref.current = comments;
  const [widgetNode] = useState(() => {
    const node = document.createElement("div");
    // The card sits inside `.monaco-editor`, whose stylesheet outlines any
    // focused textarea or button, outranking the card's own utilities. The
    // card draws its own focus rings, so it opts out of Monaco's.
    node.className = "[&_:focus]:outline-none!";
    // Monaco takes wheel events that bubble out of its widgets and scrolls
    // the code; inside the card, the wheel scrolls the thread.
    node.addEventListener("wheel", (event) => event.stopPropagation(), {
      passive: true,
    });
    return node;
  });

  useEffect(() => {
    if (!ed || !monaco) return;
    const action = ed.addAction({
      id: "forest.comment",
      label: "Comment",
      contextMenuGroupId: "navigation",
      keybindings: [
        monaco.KeyMod.CtrlCmd | monaco.KeyMod.Alt | monaco.KeyCode.KeyM,
      ],
      precondition: "editorHasSelection",
      run: (target) => {
        const model = target.getModel();
        const selection = target.getSelection();
        if (!model || !selection || selection.isEmpty()) return;
        ref.current?.onComment({
          start: model.getOffsetAt(selection.getStartPosition()),
          end: model.getOffsetAt(selection.getEndPosition()),
          quote: model.getValueInRange(selection),
        });
      },
    });
    const mouse = ed.onMouseDown((event) => {
      const model = ed.getModel();
      const line = event.target.position?.lineNumber;
      if (
        event.target.type !==
          monaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN ||
        !model ||
        !line
      ) {
        return;
      }
      const hit = ref.current?.threads.find(
        (thread) => model.getPositionAt(thread.range.start).lineNumber === line,
      );
      if (hit) ref.current?.onActivate(hit.id);
    });
    return () => {
      action.dispose();
      mouse.dispose();
    };
  }, [ed, monaco]);

  const threadsKey = JSON.stringify([comments?.threads, comments?.activeId]);
  useEffect(() => {
    const model = ed?.getModel();
    if (!ed || !monaco || !ready || !model) return;
    ed.updateOptions({ glyphMargin: true });
    const current = ref.current;
    const decorations = ed.createDecorationsCollection(
      (current?.threads ?? []).map((thread) => {
        const start = model.getPositionAt(thread.range.start);
        const end = model.getPositionAt(thread.range.end);
        const active = thread.id === current?.activeId;
        return {
          range: new monaco.Range(
            start.lineNumber,
            start.column,
            end.lineNumber,
            end.column,
          ),
          options: {
            inlineClassName: active ? HIGHLIGHT_ACTIVE : HIGHLIGHT,
            glyphMarginClassName: GLYPH,
            glyphMarginHoverMessage: { value: "Open comment" },
            stickiness:
              monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
          },
        };
      }),
    );
    return () => decorations.clear();
  }, [ed, monaco, ready, threadsKey]);

  const active = comments?.threads.find((t) => t.id === comments.activeId);
  const activeStart = active?.range.start;
  // The card renders once its widget is in the document, so its composer's
  // autofocus lands (focus() on a detached node does nothing).
  const [attached, setAttached] = useState(false);
  useEffect(() => {
    const model = ed?.getModel();
    if (!ed || !monaco || !ready || !model || activeStart === undefined) return;
    const position = model.getPositionAt(activeStart);
    const widget: editor.IContentWidget = {
      getId: () => "forest.comment-thread",
      getDomNode: () => widgetNode,
      getPosition: () => ({
        position,
        preference: [
          monaco.editor.ContentWidgetPositionPreference.BELOW,
          monaco.editor.ContentWidgetPositionPreference.ABOVE,
        ],
      }),
      allowEditorOverflow: true,
    };
    ed.addContentWidget(widget);
    ed.revealPositionInCenterIfOutsideViewport(position);
    setAttached(true);
    return () => {
      ed.removeContentWidget(widget);
      setAttached(false);
    };
  }, [ed, monaco, ready, activeStart, widgetNode]);

  return attached && active && comments?.card
    ? createPortal(comments.card, widgetNode)
    : null;
}
